import { ContainerClient } from '@azure/storage-blob';
import { useToast } from '@chakra-ui/react';
import { uploadAction } from 'actions';
import { Promise as BluebirdPromise } from 'bluebird';
import { MAX_CONCURRENT_REQUEST } from 'helpers/constant';
import { UPLOAD_FILES_STATUS } from 'helpers/enum';
import { formatBytestoKBytes, removeQueryString } from 'helpers/file';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FileStore } from 'types/actions/upload';
import { getBlobContainer } from 'utils/blob';
import { v4 as uuidv4 } from 'uuid';

const useUpload = () => {
  const toast = useToast();

  const dispatch = useDispatch();
  const { stats, status, files, blobUrl } = useSelector((state) => state.upload);

  const isUploading = status === UPLOAD_FILES_STATUS.UPLOADING;
  const progressValue = stats.total > 0 ? (stats.uploadSuccess * 100) / stats.total : 0;

  const uploadFiles = (files: File[]) => {
    dispatch(uploadAction.startUpload(files));
  };

  const uploadFileToBlobStorage = async (containerClient: ContainerClient, file: File) => {
    const blobClient = containerClient.getBlockBlobClient(uuidv4() + '_' + file.name);

    const blobHTTPHeaders = { blobContentType: file.type };

    return blobClient
      .uploadData(file, {
        blockSize: 20 * 1024 * 1024,
        concurrency: 40,
        blobHTTPHeaders,
      })
      .then(() => {
        dispatch(uploadAction.uploadSuccess());
        return {
          url: removeQueryString(blobClient.url),
          name: file.name,
          size: formatBytestoKBytes(file.size, 2),
        };
      })
      .catch(() => {
        dispatch(uploadAction.uploadFail(file));
      });
  };

  useEffect(() => {
    if (status === UPLOAD_FILES_STATUS.UPLOADING && blobUrl && files && files?.length > 0) {
      const containerClient = getBlobContainer(blobUrl);

      toast({
        title: `Start uploading ${stats.total} file`,
        status: 'success',
        position: 'bottom-right',
        isClosable: true,
      });

      const uploadedFile: FileStore[] = [];
      BluebirdPromise.map(
        files,
        async (file) => {
          return uploadFileToBlobStorage(containerClient, file).then((file) => {
            if (file) {
              uploadedFile.push(file);
            }
          });
        },
        { concurrency: MAX_CONCURRENT_REQUEST }
      )
        .then(() => {
          dispatch(uploadAction.completeUploadFile({ files: uploadedFile }));
          dispatch(uploadAction.completeUpload());
        })
        .catch((e: Error) => console.log(e));
    }
  }, [blobUrl, status, files]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (status === UPLOAD_FILES_STATUS.FINISH) {
      if (stats.uploadSuccess > 0) {
        toast({
          title: `${stats.uploadSuccess}/${stats.total} file uploaded successfully`,
          status: 'success',
          position: 'bottom-right',
          isClosable: true,
        });
      }

      if (stats.uploadFail > 0) {
        toast({
          title: `${stats.uploadFail}/${stats.total} file uploaded failed`,
          status: 'error',
          position: 'bottom-right',
          isClosable: true,
        });
      }
      dispatch(uploadAction.resetUpload());
    }

    return () => {};
  }, [status]); // eslint-disable-line react-hooks/exhaustive-deps

  return { uploadFiles, isUploading, progressValue };
};

export default useUpload;
